import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { opsApi, apiError, fmtDate, GRAD, FONT_HEAD } from './opsApi';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import Loader from '../../components/Loader';

const STATUS_STYLES = {
  open: 'bg-blue-50 text-blue-600 border-blue-100',
  in_progress: 'bg-amber-50 text-amber-600 border-amber-100',
  resolved: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  closed: 'bg-gray-50 text-gray-600 border-gray-100',
};

const PRIORITY_STYLES = {
  low: 'bg-gray-50 text-gray-500 border-gray-100',
  medium: 'bg-amber-50 text-amber-500 border-amber-100',
  high: 'bg-orange-50 text-orange-500 border-orange-100',
  urgent: 'bg-red-50 text-red-500 border-red-100',
};

const TYPE_LABELS = {
  software_issue: 'Software Issue',
  billing_question: 'Billing Question',
  account_issue: 'Account Issue',
  feature_request: 'Feature Request',
  other: 'Other',
};

function StatusBadge({ status }) {
  return (
    <span className={`text-xs px-3 py-1 rounded-full font-semibold border ${STATUS_STYLES[status] || STATUS_STYLES.open}`}>
      {(status || 'open').replace('_', ' ').toUpperCase()}
    </span>
  );
}

function PriorityBadge({ priority }) {
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium}`}>
      {(priority || 'medium').toUpperCase()}
    </span>
  );
}

function ActivityItem({ entry }) {
  const actionLabels = {
    created: 'created this ticket',
    status_changed: `changed status from "${entry.from_value}" to "${entry.to_value}"`,
    priority_changed: `changed priority from "${entry.from_value}" to "${entry.to_value}"`,
    comment_added: 'added a comment',
    assigned: 'assigned ticket',
    closed: 'closed this ticket',
    resolved: 'resolved this ticket',
  };
  return (
    <div className="flex gap-3 text-sm">
      <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: GRAD }} />
      <div>
        <span className="font-medium text-slate-700">{entry.user_name || entry.user_role || 'User'}</span>{' '}
        <span className="text-slate-400">{actionLabels[entry.action] || entry.action}</span>
        <div className="text-[10px] text-slate-300 mt-0.5">{fmtDate(entry.created_at)}</div>
      </div>
    </div>
  );
}

export default function SupportTicketDetailPage({ user }) {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [busy, setBusy] = useState(false);

  // Update form
  const [newStatus, setNewStatus] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');

  // Comment
  const [commentText, setCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await opsApi.get(`/support-tickets/${ticketId}`);
      setTicket(data);
      setNewStatus(data.status || 'open');
      setNewPriority(data.priority || 'medium');
      setResolutionNotes(data.resolution_notes || '');
    } catch (e) {
      toast.error(apiError(e));
      navigate('/sales/support-tickets');
    }
  }, [ticketId, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async () => {
    setBusy(true);
    try {
      const updates = {};
      if (newStatus !== ticket.status) updates.status = newStatus;
      if (newPriority !== ticket.priority) updates.priority = newPriority;
      if (resolutionNotes !== (ticket.resolution_notes || '')) updates.resolution_notes = resolutionNotes;

      if (Object.keys(updates).length === 0) {
        toast('No changes to save');
        setBusy(false);
        return;
      }
      await opsApi.put(`/support-tickets/${ticketId}`, updates);
      toast.success('Ticket updated');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) { toast.error('Comment cannot be empty'); return; }
    setSubmittingComment(true);
    try {
      await opsApi.post(`/support-tickets/${ticketId}/comments`, {
        text: commentText,
        is_internal: isInternal,
      });
      toast.success('Comment added');
      setCommentText('');
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setSubmittingComment(false);
  };

  if (!ticket) {
    return (
      <Loader />
    );
  }

  const publicComments = (ticket.comments || []).filter((c) => !c.is_internal);
  const internalComments = (ticket.comments || []).filter((c) => c.is_internal);

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      {/* Header */}
      <div>
        <Link to="/sales/support-tickets" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Support Tickets
        </Link>
        <div className="flex items-start gap-4 justify-between flex-wrap">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap mb-2">
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200 font-medium">
                {TYPE_LABELS[ticket.type] || ticket.type}
              </span>
            </div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900" style={FONT_HEAD}>{ticket.title}</h1>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400 flex-wrap">
              <span>From: <b className="text-slate-600">{ticket.tenant_name}</b></span>
              {ticket.institution_code && <span className="font-mono">({ticket.institution_code})</span>}
              <span>Created: {fmtDate(ticket.created_at)}</span>
              {ticket.resolved_at && <span>Resolved: {fmtDate(ticket.resolved_at)}</span>}
              <span className="font-mono text-slate-300">#{ticket.id?.slice(0, 8).toUpperCase()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: Content */}
        <div className="lg:col-span-2 space-y-5">
          {/* Description */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3" style={FONT_HEAD}>Description</h3>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* Resolution Notes */}
          {ticket.resolution_notes && (
            <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-6">
              <h3 className="text-sm font-semibold text-emerald-700 mb-2">Resolution Notes</h3>
              <p className="text-sm text-emerald-800 whitespace-pre-wrap">{ticket.resolution_notes}</p>
            </div>
          )}

          {/* Comments */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700" style={FONT_HEAD}>
                Comments ({publicComments.length})
              </h3>
              {internalComments.length > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100 font-medium">
                  {internalComments.length} internal
                </span>
              )}
            </div>
            <div className="divide-y divide-slate-50">
              {(ticket.comments || []).length === 0 ? (
                <p className="px-6 py-8 text-sm text-slate-400 text-center">No comments yet</p>
              ) : (
                (ticket.comments || []).map((c) => {
                  const isOpsComment = ['super_admin', 'zone_admin', 'state_manager', 'sales_rep', 'support_admin'].includes(c.user_role);
                  return (
                    <div key={c.id} className={`px-6 py-4 ${c.is_internal ? 'bg-amber-50/40' : ''}`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                            style={{ background: isOpsComment ? GRAD : '#6366F1' }}
                          >
                            {isOpsComment ? 'OPS' : 'INS'}
                          </div>
                          <span className="text-xs font-medium text-slate-700">
                            {c.user_name || c.user_email || (isOpsComment ? 'Ops Team' : 'Institution')}
                          </span>
                          {c.is_internal && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Internal</span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400">{fmtDate(c.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap pl-8">{c.text}</p>
                    </div>
                  );
                })
              )}
            </div>

            {/* Add Comment */}
            <div className="px-6 py-4 border-t border-slate-100 space-y-3">
              <div className="flex items-center gap-3">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Add Comment</h4>
                <label className="flex items-center gap-1.5 ml-auto cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                    className="rounded"
                    data-testid="internal-comment-toggle"
                  />
                  <span className="text-xs text-slate-500">Internal only</span>
                </label>
              </div>
              <Textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                rows={3}
                placeholder={isInternal ? 'Internal note — not visible to institution...' : 'Write a reply to the institution...'}
                className="text-sm resize-none"
                data-testid="ops-comment-input"
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleAddComment}
                  disabled={submittingComment || !commentText.trim()}
                  className="h-9 px-4 text-xs font-semibold text-white rounded-xl border-0"
                  style={{ background: GRAD }}
                  data-testid="submit-ops-comment"
                >
                  {submittingComment ? 'Sending...' : isInternal ? 'Add Internal Note' : 'Send Reply'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Controls + Activity */}
        <div className="space-y-5">
          {/* Update Controls */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-700" style={FONT_HEAD}>Update Ticket</h3>

            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="h-9 text-sm" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Priority</label>
              <Select value={newPriority} onValueChange={setNewPriority}>
                <SelectTrigger className="h-9 text-sm" data-testid="priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                Resolution Notes
              </label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={3}
                placeholder="Describe how the issue was resolved..."
                className="text-sm resize-none"
                data-testid="resolution-notes"
              />
            </div>

            <Button
              onClick={handleUpdate}
              disabled={busy}
              className="w-full h-9 text-xs font-semibold text-white rounded-xl border-0"
              style={{ background: GRAD }}
              data-testid="save-ticket-btn"
            >
              {busy ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>

          {/* Institution Info */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3" style={FONT_HEAD}>Institution</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Name</span>
                <span className="font-medium text-slate-700">{ticket.tenant_name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Code</span>
                <span className="font-mono text-slate-600 text-xs">{ticket.institution_code || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Raised by</span>
                <span className="text-slate-600 text-xs truncate ml-2">{ticket.created_by_email || '—'}</span>
              </div>
              {ticket.created_by_ops && (
                <div className="text-[10px] px-2 py-1 rounded bg-slate-50 text-slate-400 text-center mt-2">
                  Created by Ops team
                </div>
              )}
            </div>
          </div>

          {/* Activity Log */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4" style={FONT_HEAD}>Activity</h3>
            <div className="space-y-3">
              {(ticket.activity_log || []).length === 0 ? (
                <p className="text-xs text-slate-400">No activity yet</p>
              ) : (
                (ticket.activity_log || []).map((entry, i) => (
                  <ActivityItem key={i} entry={entry} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
